













































































































































































































































import Vue from 'vue'
import {
  defineComponent, ref, reactive, onMounted, getCurrentInstance,
} from '@vue/composition-api'
import * as XLSX from 'xlsx'
import {
  mdiDownload, mdiFileDocumentOutline, mdiChartLine, mdiFileDocumentMultipleOutline, mdiCashMinus,
} from '@mdi/js'
import pLimit from 'p-limit'
import EodClosingAggregationApi from '@/api/v2/eod-closing/Aggregation'
import EodReportAggregationApi from '@/api/v2/eod-report/EodReportClubAggregation'
import { fetchAggregationContainers, fetchAggregationContainer } from '@/api/v2/report/AggregationContainer'
import { handleApiError, ApiError } from '@/utils/errorHandler'
import DateRangePicker from '@/views/v2-temp/common/components/DateRangePicker.vue'
import { useDateRange } from '@/views/v2-temp/common/composables/useDateRange'
import { useAggregationJobs } from './composables/useAggregationJobs'
import {
  buildWorksheetFromAggregation, createMetaSheet, generateUniqueSheetName, hashId, AggregationMeta,
} from './composables/useExcel'
import { getExpenses } from '@/api/v2/data-export/Expense'
import useTaxCalculation from '@/views/v2-temp/report/expenses/composables/useTaxCalculation'

type SectionType = 'closing' | 'report' | 'custom'
type DownloadDialogMode = 'existing' | 'reaggregate'
type DownloadDialogKind = 'single' | 'section'

interface AggregationRecord {
  id: number
  name: string
  description: string
  type?: string
  container?: boolean
  key: string
  section: SectionType
}
interface RecordsBySection {
  closing: AggregationRecord[]
  report: AggregationRecord[]
  custom: AggregationRecord[]
}
interface Ranges { [key: string]: [string, string] }
interface Downloadings { [key: string]: boolean }
interface BulkRanges {
  closing: [string, string]
  report: [string, string]
  custom: [string, string]
}
interface BulkDownloading {
  closing: boolean
  report: boolean
  custom: boolean
}
interface SectionTitles {
  closing: string
  report: string
  custom: string
}
interface DownloadDialogState {
  open: boolean
  mode: DownloadDialogMode
  loading: boolean
  kind: DownloadDialogKind | null
  rec: AggregationRecord | null
  section: SectionType | null
}

const META_SHEET_NAME = '____meta____'

export default defineComponent({
  name: 'AggregationExcelIndex',
  components: { DateRangePicker },
  setup() {
    const vm = getCurrentInstance()!.proxy
    const { today } = useDateRange()
    const { ensure } = useAggregationJobs()
    const { getTaxRateText, calculateTaxAmount } = useTaxCalculation()

    const loading = ref<boolean>(true)
    const recordsBySection = reactive<RecordsBySection>({
      closing: [],
      report: [],
      custom: [],
    })
    const sectionOrder: SectionType[] = ['closing', 'report', 'custom']
    const sectionTitles: SectionTitles = {
      closing: '日計',
      report: '日報',
      custom: 'カスタムレポート',
    }
    const ranges = reactive<Ranges>({})
    const downloadings = reactive<Downloadings>({})
    const bulkRanges = reactive<BulkRanges>({
      closing: [today.value, today.value],
      report: [today.value, today.value],
      custom: [today.value, today.value],
    })
    const bulkDownloading = reactive<BulkDownloading>({
      closing: false,
      report: false,
      custom: false,
    })
    const downloadDialog = reactive<DownloadDialogState>({
      open: false,
      mode: 'existing',
      loading: false,
      kind: null,
      rec: null,
      section: null,
    })
    const expenseRanges = ref<[string, string]>([today.value, today.value])
    const expenseLoading = ref<boolean>(false)

    // アイコン
    const getSectionIcon = (section: SectionType): string => {
      const icons: Record<SectionType, string> = {
        closing: mdiFileDocumentOutline,
        report: mdiChartLine,
        custom: mdiFileDocumentMultipleOutline,
      }

      return icons[section] || mdiFileDocumentOutline
    }
    const getRecordIcon = (rec: AggregationRecord): string => (rec.container ? mdiFileDocumentMultipleOutline : mdiFileDocumentOutline)

    const addRecord = (section: SectionType, rec: Omit<AggregationRecord, 'key' | 'section'>): void => {
      const key = `${section}-${rec.id}`
      recordsBySection[section].push({ ...rec, key, section })
      Vue.set(ranges, key, [today.value, today.value])
      Vue.set(downloadings, key, false)
    }

    const load = async (): Promise<void> => {
      loading.value = true
      try {
        const [closingRes, reportRes, containers] = await Promise.allSettled([
          EodClosingAggregationApi.getAggregations(today),
          EodReportAggregationApi.getAggregations(today),
          fetchAggregationContainers(),
        ])

        if (closingRes.status === 'fulfilled' && closingRes.value?.data?.data?.data) {
          closingRes.value.data.data.data.forEach((r: any) => {
            addRecord('closing', {
              id: r.attributes.id,
              name: r.attributes.name,
              description: r.attributes.description,
              type: r.attributes.type,
            })
          })
        }

        if (reportRes.status === 'fulfilled' && reportRes.value?.data?.data?.data) {
          reportRes.value.data.data.data.forEach((r: any) => {
            addRecord('report', {
              id: r.attributes.id,
              name: r.attributes.name,
              description: r.attributes.description,
              type: r.attributes.type,
            })
          })
        }

        if (containers.status === 'fulfilled' && Array.isArray(containers.value)) {
          containers.value.forEach((c: any) => {
            addRecord('custom', {
              id: c.id,
              name: c.name,
              description: c.description,
              container: true,
            })
          })
        }
      } catch (e) {
        // ここには基本来ない（allSettled）が、念のため
        handleApiError(e as ApiError, { $toast: vm.$toast })
      } finally {
        loading.value = false
      }
    }

    // === ダウンロード ===
    const doDownload = async (
      rec: AggregationRecord,
      range: [string, string],
      opts: { forceReload?: boolean } = { forceReload: false },
    ): Promise<XLSX.WorkBook> => {
      const wb = XLSX.utils.book_new()
      const metaData: AggregationMeta[] = []

      if (rec.container) {
        const container = await fetchAggregationContainer(rec.id)
        const containerAggregations: Array<{ id: number; name: string }> = container.rows.data.flatMap((row: any) => row.attributes.cols.data.flatMap((col: any) => col.attributes.aggregations.data.map((agg: any) => ({
          id: agg.attributes.id,
          name: agg.attributes.name,
        }))))

        // 並列数制限（3並列）で高速かつ安定
        const limit = pLimit(3)
        await Promise.all(
          containerAggregations.map(a => limit(async () => {
            const data = await ensure(a.id, range, opts)
            if (data) {
              const ws = buildWorksheetFromAggregation(data)
              const sheetName = generateUniqueSheetName(a.name, wb.SheetNames)
              XLSX.utils.book_append_sheet(wb, ws, sheetName)
              metaData.push({ uid: hashId(a.id, (vm as any).currentClub.slug), sheetName })
            }
          })),
        )
      } else {
        const data = await ensure(rec.id, range, opts)
        if (data) {
          const ws = buildWorksheetFromAggregation(data)
          const sheetName = generateUniqueSheetName(rec.name, wb.SheetNames)
          XLSX.utils.book_append_sheet(wb, ws, sheetName)
          metaData.push({ uid: hashId(rec.id, (vm as any).currentClub.slug), sheetName })
        }
      }

      if (metaData.length > 0) {
        const metaSheet = createMetaSheet(metaData)
        XLSX.utils.book_append_sheet(wb, metaSheet, META_SHEET_NAME)
        wb.SheetNames.unshift(wb.SheetNames.pop()!)
      }

      return wb
    }

    // 経費専用のダウンロード処理
    const downloadExpense = async (): Promise<void> => {
      expenseLoading.value = true
      try {
        const [startDate, endDate] = expenseRanges.value
        const expenses = await getExpenses({
          start_date: startDate,
          end_date: endDate,
        })

        const wb = XLSX.utils.book_new()
        const ws = XLSX.utils.json_to_sheet(expenses.map(expense => {
          const taxRateText = getTaxRateText(expense.taxRate || 'blank')
          // 保存時に確定したスナップショットを優先し、無い過去データはフロント計算で補完
          const taxAmount = expense.taxAmount ?? calculateTaxAmount(expense.amount, expense.taxRate || 'blank')
          const categoryTextMap: Record<string, string> = { qualified: '適格', unregistered: '未登録' }
          const categoryText = (expense.supplierInvoiceCategory && categoryTextMap[expense.supplierInvoiceCategory]) || ''

          return {
            日付: expense.date,
            主分類: expense.expensePrimaryLabelName,
            副分類: expense.expenseSecondaryLabelName,
            第三分類: expense.expenseTertiaryLabelName,
            第四分類: expense.expenseQuaternaryLabelName,
            金額: expense.amount,
            消費税率: taxRateText,
            消費税額: taxAmount !== null ? taxAmount : '',
            取引先: expense.expenseSupplierName || '',
            インボイス区分: categoryText,
            控除率: expense.deductionRatePercent != null ? `${expense.deductionRatePercent}%` : '',
            控除対象額: expense.deductibleTaxAmount ?? '',
            非現金: !!expense.isNonCash,
          }
        }))
        XLSX.utils.book_append_sheet(wb, ws, '経費')

        XLSX.writeFile(wb, `経費データ ${startDate}~${endDate}.xlsx`)
      } catch (e) {
        handleApiError(e as ApiError, { $toast: vm.$toast })
      } finally {
        expenseLoading.value = false
      }
    }

    const download = async (rec: AggregationRecord, forceReload: boolean = false): Promise<void> => {
      downloadings[rec.key] = true
      try {
        const wb = await doDownload(rec, ranges[rec.key], { forceReload })
        const [startDate, endDate] = ranges[rec.key]
        XLSX.writeFile(wb, `${rec.name} ${startDate}~${endDate}.xlsx`)
      } catch (e) {
        handleApiError(e as ApiError, { $toast: vm.$toast })
      } finally {
        downloadings[rec.key] = false
      }
    }

    const downloadSection = async (section: SectionType, forceReload: boolean = false): Promise<void> => {
      bulkDownloading[section] = true
      const wb = XLSX.utils.book_new()
      const allMetaData: AggregationMeta[] = []

      try {
        const limit = pLimit(3)
        const subs = await Promise.all(
          recordsBySection[section].map(rec => limit(() => doDownload(rec, bulkRanges[section], { forceReload }))),
        )

        // 各サブワークブックを統合し、メタデータを収集
        subs.forEach(sub => {
          // メタデータシートからメタデータを取得
          const metaSheet = sub.Sheets[META_SHEET_NAME]
          const sourceMetaData: AggregationMeta[] = []
          if (metaSheet) {
            const metaRange = XLSX.utils.decode_range(metaSheet['!ref'] || 'A1:B1')
            for (let row = metaRange.s.r + 1; row <= metaRange.e.r; row += 1) {
              const uidCell = metaSheet[XLSX.utils.encode_cell({ r: row, c: 0 })]
              const sheetNameCell = metaSheet[XLSX.utils.encode_cell({ r: row, c: 1 })]
              if (uidCell && sheetNameCell) {
                sourceMetaData.push({
                  uid: uidCell.v.toString(),
                  sheetName: sheetNameCell.v,
                })
              }
            }
          }

          // メタデータシート以外を統合
          const subSheets = sub.SheetNames.filter(name => name !== META_SHEET_NAME)
          subSheets.forEach(name => {
            const ws = sub.Sheets[name]
            const sheetName = generateUniqueSheetName(name, wb.SheetNames)
            XLSX.utils.book_append_sheet(wb, ws, sheetName)

            // メタデータのシート名を更新
            const metaItem = sourceMetaData.find(item => item.sheetName === name)
            if (metaItem) {
              allMetaData.push({
                uid: metaItem.uid,
                sheetName,
              })
            }
          })
        })

        // 統合されたメタデータシートを最初に追加
        if (allMetaData.length > 0) {
          const metaSheet = createMetaSheet(allMetaData)
          XLSX.utils.book_append_sheet(wb, metaSheet, META_SHEET_NAME)
          // メタデータシートを最初の位置に移動
          wb.SheetNames.unshift(wb.SheetNames.pop()!)
        }

        const [startDate, endDate] = bulkRanges[section]
        XLSX.writeFile(wb, `${sectionTitles[section]} ${startDate}~${endDate}.xlsx`)
      } catch (e) {
        handleApiError(e as ApiError, { $toast: vm.$toast })
      } finally {
        bulkDownloading[section] = false
      }
    }

    const openDownloadDialog = (payload: { kind: DownloadDialogKind; rec?: AggregationRecord; section?: SectionType }) => {
      downloadDialog.open = true
      downloadDialog.mode = 'existing'
      downloadDialog.loading = false
      downloadDialog.kind = payload.kind
      downloadDialog.rec = payload.rec ?? null
      downloadDialog.section = payload.section ?? null
    }

    const closeDownloadDialog = () => {
      downloadDialog.open = false
      downloadDialog.loading = false
      downloadDialog.kind = null
      downloadDialog.rec = null
      downloadDialog.section = null
    }

    const confirmDownloadDialog = async () => {
      if (!downloadDialog.kind) return
      downloadDialog.loading = true
      const force = downloadDialog.mode === 'reaggregate'
      try {
        if (downloadDialog.kind === 'single' && downloadDialog.rec) {
          await download(downloadDialog.rec, force)
        } else if (downloadDialog.kind === 'section' && downloadDialog.section) {
          await downloadSection(downloadDialog.section, force)
        }
        closeDownloadDialog()
      } catch (e) {
        handleApiError(e as ApiError, { $toast: vm.$toast })
        downloadDialog.loading = false
      }
    }

    onMounted(load)

    return {
      loading,
      sectionOrder,
      sectionTitles,
      recordsBySection,
      ranges,
      downloadings,
      bulkRanges,
      bulkDownloading,
      download,
      downloadSection,
      downloadDialog,
      openDownloadDialog,
      closeDownloadDialog,
      confirmDownloadDialog,
      getSectionIcon,
      getRecordIcon,
      expenseRanges,
      expenseLoading,
      downloadExpense,
      icons: {
        mdiDownload,
        mdiCashMinus,
      },
    }
  },
})
